import { useCallback, useMemo, useRef, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const PdfJS = require("pdfjs-dist/legacy/build/pdf.min");
import CONFIG from "config/config";
import { getSignedUrl } from "graphQL/query/file";
import { insertCampaignLogs } from "Logs/campaign/gql";
import { CAMPAIGN_LOG_ENUM_TYPES } from "shared/constants";
import { authorization } from "module/auth";
import useDesignManagement, { type Design } from "pages/creative/pageV2/useDesignManagement";
import type { ChannelConfig } from "../config";

PdfJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PdfJS.version}/pdf.worker.min.js`;

/** Front and back: the most pages a design PDF is rendered — and, for ADM, allowed — to have. */
export const MAX_PREVIEW_PAGES = 2;
const THUMBNAIL_MARGIN = 20;

export type DesignUploadStatus = "idle" | "rendering" | "ready" | "committing" | "error";

export interface DesignPreviewPage {
  pageNumber: number;
  dataUrl: string;
  displayWidth: number;
  displayHeight: number;
  /** Raw page size in PDF points (1 pt = 1/72 in) for dimension validation. */
  widthPt: number;
  heightPt: number;
}

export interface DesignUploadPreview {
  file: File;
  pages: DesignPreviewPage[];
}

export interface RenderedPdfDocument {
  /** The first MAX_PREVIEW_PAGES pages, rendered. */
  pages: DesignPreviewPage[];
  /** How many pages the PDF really has — the preview never shows more than MAX_PREVIEW_PAGES. */
  numPages: number;
}

export interface DesignUploadMessages {
  /** Copy for a file over the size limit; receives the configured MB cap. */
  fileTooLarge: (maxFileSizeMb: number) => string;
  /** Copy for a non-accepted file type (e.g. not a PDF). */
  invalidType: string;
  /** Copy when the PDF can't be rendered/read. */
  renderFailed: string;
  /** Copy when the S3/createDesign commit fails. */
  uploadFailed: string;
}

// Defaults preserve the ADM wording; H2H/D2D pass the spec strings (AC44/AC45).
const DEFAULT_MESSAGES: DesignUploadMessages = {
  fileTooLarge: (mb) => `The file exceeded the ${mb} MB limit.`,
  invalidType: "Unsupported file type. Please upload a PDF file.",
  renderFailed: "We couldn't read that PDF. Please try a different file.",
  uploadFailed: "There was an error while uploading the file. Please try again.",
};

export interface UseDesignUploadOptions {
  design: ChannelConfig["design"];
  format: string;
  campaignId?: string;
  clientId?: string | null;
  /** Override the inline error copy per channel. Unset keys fall back to the ADM defaults. */
  messages?: Partial<DesignUploadMessages>;
  /**
   * Optional post-render check on the parsed pages (e.g. artwork dimension
   * validation, PAD-32228). `numPages` is the document's real page count,
   * which can exceed the rendered pages. Return an error message to reject
   * the file, or null to accept it.
   */
  validatePages?: (pages: DesignPreviewPage[], numPages: number) => string | null;
}

export const renderPdfDocument = async (source: File | string): Promise<RenderedPdfDocument> => {
  const isUrl = typeof source === "string";
  const fileUrl = isUrl ? source : URL.createObjectURL(source);
  try {
    const pdf = await PdfJS.getDocument(fileUrl).promise;
    const totalPages = Math.min(pdf.numPages, MAX_PREVIEW_PAGES);
    const pages: DesignPreviewPage[] = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;

      const maxWidth = 300;
      const maxHeight = 200;
      const aspectRatio = viewport.width / viewport.height;
      let displayWidth: number;
      let displayHeight: number;
      if (aspectRatio > maxWidth / maxHeight) {
        displayWidth = maxWidth;
        displayHeight = maxWidth / aspectRatio;
      } else {
        displayHeight = maxHeight;
        displayWidth = maxHeight * aspectRatio;
      }

      pages.push({
        pageNumber: pageNum,
        dataUrl: canvas.toDataURL("image/png", 0.8),
        displayWidth,
        displayHeight,
        widthPt: viewport.width,
        heightPt: viewport.height,
      });
    }
    return { pages, numPages: pdf.numPages };
  } finally {
    if (!isUrl) URL.revokeObjectURL(fileUrl);
  }
};

/** The rendered preview pages only — for galleries that show what is stored. */
export const renderPdfPages = async (source: File | string): Promise<DesignPreviewPage[]> =>
  (await renderPdfDocument(source)).pages;

const buildThumbnail = (pages: DesignPreviewPage[]): Promise<File> =>
  new Promise((resolve, reject) => {
    if (pages.length === 0) {
      reject(new Error("No pages to render"));
      return;
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    const totalWidth = pages.reduce((sum, page) => sum + page.displayWidth, 0) + (pages.length - 1) * THUMBNAIL_MARGIN;
    const maxHeight = Math.max(...pages.map((page) => page.displayHeight));
    canvas.width = totalWidth;
    canvas.height = maxHeight;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, totalWidth, maxHeight);

    const images: HTMLImageElement[] = [];
    let loaded = 0;

    pages.forEach((page, index) => {
      const img = new Image();
      img.onload = () => {
        images[index] = img;
        loaded += 1;
        if (loaded === pages.length) {
          let currentX = 0;
          images.forEach((image, i) => {
            const p = pages[i];
            ctx.drawImage(image, currentX, (maxHeight - p.displayHeight) / 2, p.displayWidth, p.displayHeight);
            currentX += p.displayWidth + THUMBNAIL_MARGIN;
          });
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(new File([blob], "thumbnail.png", { type: "image/png" }));
              } else {
                reject(new Error("Could not create thumbnail blob"));
              }
            },
            "image/png",
            0.8
          );
        }
      };
      img.onerror = () => reject(new Error(`Failed to load preview page ${index + 1}`));
      img.src = page.dataUrl;
    });
  });

const putToS3 = async (key: string, body: File): Promise<string> => {
  const putUrl = await getSignedUrl(CONFIG.PDF_FLYER_BUCKET, key, "PUT", CONFIG.PDF_FLYER_BUCKET_REGION, "public-read");
  const response = await fetch(putUrl, {
    method: "PUT",
    body,
    headers: { "Content-Type": body.type },
  });
  if (!response.ok) {
    throw new Error(`Failed to upload ${body.name}`);
  }
  return getSignedUrl(CONFIG.PDF_FLYER_BUCKET, key, "GET", CONFIG.PDF_FLYER_BUCKET_REGION, "public-read");
};

/**
 * Channel-agnostic design upload. Splits the legacy on-drop pipeline into
 * renderPreview (local pdf render + thumbnail, no network) and commitUpload
 * (S3 puts + createDesign), so nothing is persisted until the user confirms.
 */
export const useDesignUpload = ({
  design,
  format,
  campaignId,
  clientId,
  messages,
  validatePages,
}: UseDesignUploadOptions) => {
  const user = authorization.getUser();
  const { createDesign } = useDesignManagement({ clientId: clientId || user.clientId || "" });
  const msg: DesignUploadMessages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messages }), [messages]);

  const [status, setStatus] = useState<DesignUploadStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DesignUploadPreview | null>(null);
  const thumbnailRef = useRef<File | null>(null);
  const renderTokenRef = useRef(0);

  const maxBytes = design.maxFileSizeMb * 1048576;

  const validate = useCallback(
    (file: File): string | null => {
      if (design.acceptedFileTypes.length && !design.acceptedFileTypes.includes(file.type)) {
        return msg.invalidType;
      }
      if (file.size > maxBytes) {
        return msg.fileTooLarge(design.maxFileSizeMb);
      }
      return null;
    },
    [design.acceptedFileTypes, design.maxFileSizeMb, maxBytes, msg]
  );

  const reset = useCallback(() => {
    renderTokenRef.current += 1;
    thumbnailRef.current = null;
    setPreview(null);
    setError(null);
    setStatus("idle");
  }, []);

  const renderPreview = useCallback(
    async (file: File) => {
      const validationError = validate(file);
      if (validationError) {
        thumbnailRef.current = null;
        setPreview(null);
        setError(validationError);
        setStatus("error");
        return;
      }
      const token = (renderTokenRef.current += 1);
      setError(null);
      setStatus("rendering");
      try {
        const { pages, numPages } = await renderPdfDocument(file);
        const pagesError = validatePages ? validatePages(pages, numPages) : null;
        if (pagesError) {
          if (token !== renderTokenRef.current) return; // cancelled mid-render
          thumbnailRef.current = null;
          setPreview(null);
          setError(pagesError);
          setStatus("error");
          return;
        }
        const thumbnail = await buildThumbnail(pages);
        if (token !== renderTokenRef.current) return; // cancelled mid-render
        thumbnailRef.current = thumbnail;
        setPreview({ file, pages });
        setStatus("ready");
      } catch (err) {
        console.error("renderPreview error", err);
        if (token !== renderTokenRef.current) return; // cancelled mid-render
        thumbnailRef.current = null;
        setPreview(null);
        setError(msg.renderFailed);
        setStatus("error");
      }
    },
    [validate, msg, validatePages]
  );

  const commitUpload = useCallback(async (): Promise<Design | null> => {
    const file = preview?.file;
    const thumbnail = thumbnailRef.current;
    if (!file || !thumbnail) return null;

    setStatus("committing");
    setError(null);
    try {
      const stamp = Date.now().toString();
      const pdfUrl = await putToS3(`campaign-builder/public/${stamp}/${file.name}`, file);
      const thumbnailUrl = await putToS3(`campaign-builder/public/${stamp}/thumbnail.png`, thumbnail);

      const created = await createDesign({
        filename: file.name,
        s3Link: pdfUrl,
        thumbnailUrl,
        isUploaded: true,
        format,
        authorId: user.id,
      });

      if (campaignId) {
        await insertCampaignLogs([
          {
            campaignId: `${campaignId}`,
            type: CAMPAIGN_LOG_ENUM_TYPES.UPLOAD_PDF_DESIGN,
            additionalInfo: `[CB] CAMPAIGN EDIT File name: ${file.name}`,
          },
        ]);
      }

      setStatus("idle");
      setPreview(null);
      thumbnailRef.current = null;
      return created;
    } catch (err) {
      console.error("commitUpload error", err);
      setError(msg.uploadFailed);
      setStatus("error");
      return null;
    }
  }, [preview, createDesign, format, user.id, campaignId, msg]);

  const onDropRejected = useCallback(
    (fileRejections: { errors: { code: string }[] }[]) => {
      const codes = fileRejections.flatMap((rejection) => rejection.errors.map((e) => e.code));
      if (codes.includes("file-too-large")) {
        setError(msg.fileTooLarge(design.maxFileSizeMb));
      } else if (codes.includes("file-invalid-type")) {
        setError(msg.invalidType);
      } else {
        setError("That file couldn't be accepted.");
      }
      setStatus("error");
    },
    [design.maxFileSizeMb, msg]
  );

  return {
    status,
    error,
    preview,
    isRendering: status === "rendering",
    isCommitting: status === "committing",
    maxBytes,
    renderPreview,
    commitUpload,
    reset,
    clearError: () => setError(null),
    onDrop: (acceptedFiles: File[]) => acceptedFiles[0] && renderPreview(acceptedFiles[0]),
    onDropRejected,
  };
};
